import { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { cloneDeep, isEmpty, isEqual, isNil, isObject, noop, sortBy } from 'lodash';
// Import Types
import type { FromToBlockItem, FromToBlockRef } from './types';
import type { DependencyPopoverPosition } from 'components/common/FilterDependencyDots';
// Import UI Components
import { Checkbox, Empty, ExtendedInput } from '@geneui/components';
// Import Components
import PreviewRow from './PreviewRow';
import ParentRow from './ParentRow';
import Row from './Row';
// Import Hooks
import usePrevious from 'hooks/UsePrevious';
// Import Constants
import { l } from 'constants/common';
// Import Services
import { getParentState, StateValues, States } from './service';

interface Props {
    title: string;
    hasInfoTooltip?: boolean;
    countsLabel?: string;
    blockList?: FromToBlockItem[];
    withParent?: boolean;
    getUpdate?: (blockItems: FromToBlockItem[], selectedItemsCount: number, counter: number) => void;
    resetSearch?: string;
    withPosition?: boolean;
    withSelect?: boolean;
    withSearch?: boolean;
    withDisabled?: boolean;
    /** Side the row dependency/info popovers open on (the right-hand block should use `left`). */
    popoverPosition?: DependencyPopoverPosition;
}

const Block = forwardRef<FromToBlockRef, Props>((props, ref) => {
    const {
        blockList = [],
        getUpdate = noop,
        withParent,
        withPosition,
        countsLabel,
        title,
        hasInfoTooltip,
        withSelect = true,
        withSearch = true,
        withDisabled,
        popoverPosition = 'right',
    } = props;
    const { t } = useTranslation();

    const [listItemsCount, setListItemsCount] = useState(0);
    const [selectedItemsCount, setSelectedItemsCount] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [isAllSelected, setIsAllSelected] = useState<Nullable<boolean>>(false);
    const [blockItems, setBlockItems] = useState<FromToBlockItem[]>(blockList);

    const prevBlockItems = usePrevious(blockItems);

    useImperativeHandle(ref, () => ({
        resetSearch: () => {
            setSearchInput(() => '');
        },
    }));

    useEffect(() => {
        setBlockItems(() => {
            let tmpBlockList = cloneDeep(blockList);
            if (withParent) {
                tmpBlockList.forEach((item) => {
                    item.isVisible = getParentVisibility(item.childrenList);
                });
            } else if (withPosition) {
                tmpBlockList = sortBy(tmpBlockList, ['index']);
            }
            return tmpBlockList;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blockList]);

    const getParentVisibility = (childrenList) => {
        return !isEmpty(childrenList) && childrenList.some((item) => item.isVisible);
    };

    const getItemVisibility = ({ name, searchKey = null }: FromToBlockItem, searchInputNormal: string) => {
        return `${searchKey || name}`.toLowerCase().includes(searchInputNormal);
    };

    const handleSelectedAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.currentTarget;
        setIsAllSelected(checked);

        setBlockItems((prev) => {
            const tmpBlockItems = cloneDeep(prev);
            tmpBlockItems.forEach((item) => {
                if (item.isVisible && !item.isPermanent) {
                    if (!withDisabled || !item.isDisabled) {
                        item.isChecked = checked;
                    }
                    if (withParent) {
                        item.childrenList?.forEach((row) => {
                            if (row.isVisible && (!withDisabled || !row.isDisabled)) {
                                row.isChecked = checked;
                            }
                        });
                    }
                }
            });

            return tmpBlockItems;
        });
    };

    const isVisibleData = (tmpListItems) => {
        for (const item of tmpListItems) {
            if (withParent) {
                for (const row of item.childrenList) {
                    if (row.isVisible) {
                        return true;
                    }
                }
            } else if (item.isVisible) {
                return true;
            }
        }
        return false;
    };

    const isVisibleSelectAll = (listItems) => {
        const tmpListItems = listItems.filter((item) => !item.isPermanent && (!withDisabled || !item.isDisabled));
        return isVisibleData(tmpListItems);
    };

    const blockListChanges = (blockItems) => {
        let counter = 0;
        let selectedCounter = 0;
        let tmpState = States.Invisible;

        blockItems.forEach((item) => {
            if (item.isVisible && !item.isPermanent && (!withDisabled || !item.isDisabled)) {
                if (item.isChecked) {
                    tmpState |= States.Checked;
                } else if (isNil(item.isChecked)) {
                    tmpState |= States.Intermediate;
                } else {
                    tmpState |= States.Unchecked;
                }
            }

            if (withParent) {
                item.childrenList.forEach((row) => {
                    if (row.isChecked) {
                        selectedCounter++;
                    }
                    counter++;
                });
            } else {
                if (item.isChecked) {
                    selectedCounter++;
                }
                counter++;
            }
        });

        setIsAllSelected(StateValues[tmpState]);
        setSelectedItemsCount(() => selectedCounter);
        setListItemsCount(() => counter);

        if (!isEqual(prevBlockItems, blockItems)) {
            getUpdate(blockItems, selectedCounter, counter);
        }
    };

    useEffect(() => {
        blockListChanges(blockItems);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blockItems]);

    const searchHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = isObject(e) ? e.target.value : e;
        setSearchInput(value);
    };

    useEffect(() => {
        const searchInputNormal = searchInput.trim().toLowerCase();

        setBlockItems((prev) => {
            const tmpBlockItems = cloneDeep(prev);

            tmpBlockItems.forEach((item) => {
                if (withParent) {
                    item.childrenList?.forEach((childItem) => {
                        childItem.isVisible = getItemVisibility(childItem, searchInputNormal);
                    });
                    item.isVisible = getParentVisibility(item.childrenList);
                    item.isChecked = getParentState(item.childrenList);
                } else {
                    item.isVisible = getItemVisibility(item, searchInputNormal);
                }
            });
            return tmpBlockItems;
        });
    }, [searchInput, withParent]);

    const handleRowClick = (index: number) => {
        return (isChecked: boolean) => {
            setBlockItems((prev) => {
                const tmpBlockItems = cloneDeep(prev);
                tmpBlockItems[index].isChecked = isChecked;
                if (withParent) {
                    tmpBlockItems[index].childrenList?.forEach((item) => {
                        if (item.isVisible && (!withDisabled || !item.isDisabled)) {
                            item.isChecked = isChecked;
                        }
                    });
                }
                return tmpBlockItems;
            });
        };
    };

    const handleParentRowClick = (parentIndex: number) => {
        return (index: number) => {
            return (checked: boolean) => {
                setBlockItems((prev) => {
                    const tmpBlockItems = cloneDeep(prev);
                    const tmpChildrenList = tmpBlockItems[parentIndex].childrenList;
                    if (tmpChildrenList?.[index]) {
                        tmpChildrenList[index].isChecked = checked;
                    }
                    tmpBlockItems[parentIndex].isChecked = getParentState(tmpChildrenList);
                    return tmpBlockItems;
                });
            };
        };
    };

    const handleRowDrop = (from, to) => {
        if (from.index !== to.index) {
            setBlockItems((prev) => {
                const tmpBlockItems = cloneDeep(prev);
                tmpBlockItems[from.index] = { ...to, index: from.index };
                tmpBlockItems[to.index] = { ...from, index: to.index };
                return tmpBlockItems;
            });
        }
    };

    const getDraggingRow = (index) => () => ({ ...blockItems[index], index });

    const visibleBlockItems = useMemo(
        () => blockItems.map((item, index) => ({ item, index })).filter(({ item }) => item.isVisible),
        [blockItems],
    );

    const isCheckboxDisabled = useMemo(
        () => visibleBlockItems.every(({ item }) => withDisabled && item.isDisabled),
        [visibleBlockItems, withDisabled],
    );

    return (
        <div className="drag-and-drop-inner">
            <div className="d-a-d-top">
                <p className="drag-and-drop-heading">{title}</p>
                {withSelect && (
                    <p>
                        {countsLabel} (<span>{selectedItemsCount}</span>/<span>{listItemsCount}</span>)
                    </p>
                )}
            </div>
            {withSearch && (
                <div className="drag-and-drop-search">
                    <ExtendedInput
                        placeholder={t(l.Search)}
                        canClear={false}
                        onChange={searchHandler}
                        value={searchInput}
                        icon="bc-icon-search"
                    />
                </div>
            )}

            <div className="drag-and-drop-choose">
                {listItemsCount > 0 && isVisibleData(blockItems) ? (
                    <>
                        {withSelect && (
                            <div className="d-a-d-select-all">
                                {isVisibleSelectAll(blockItems) && (
                                    <Checkbox
                                        label={t(l.SelectAll)}
                                        checked={isAllSelected ?? false}
                                        indeterminate={isNil(isAllSelected)}
                                        onChange={handleSelectedAllChange}
                                        disabled={isCheckboxDisabled}
                                    />
                                )}
                            </div>
                        )}

                        <div className={classNames('d-a-d-list', { dragging: false })}>
                            <div className="d-a-d-scrollable-cont">
                                {visibleBlockItems.map(({ item, index }) => {
                                    return withParent ? (
                                        <div key={index}>
                                            <ParentRow
                                                {...item}
                                                name={item.label ?? item.name}
                                                searchInput={searchInput}
                                                onClick={handleRowClick(index)}
                                                onRowClick={handleParentRowClick(index)}
                                                hasInfoTooltip={hasInfoTooltip}
                                                withSelect={withSelect}
                                                withDisabled={withDisabled}
                                                popoverPosition={popoverPosition}
                                            />
                                        </div>
                                    ) : (
                                        <Row
                                            key={`${item.id || item.searchKey || item.name}_${item.parentId}`}
                                            isDraggable={withPosition}
                                            {...item}
                                            onClick={handleRowClick(index)}
                                            dropRow={handleRowDrop}
                                            getDraggingRow={getDraggingRow(index)}
                                            hasInfoTooltip={hasInfoTooltip}
                                            withSelect={withSelect}
                                            withDisabled={withDisabled}
                                            popoverPosition={popoverPosition}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </>
                ) : (
                    <Empty appearance="greyscale" type="data" title={t(l.NoDataToDisplay)} />
                )}
            </div>

            <PreviewRow />
        </div>
    );
});

Block.displayName = 'Block';

export default Block;
